import { SPAN_CLS } from "./fieldTypes";
import { FileUploader } from "./FormFiles";
import { PhoneInput } from "@/components/PhoneInput";

const inputCls = "w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/15 disabled:bg-slate-50";

function Choice({ f, value, onChange, multi }) {
  const vals = multi ? (Array.isArray(value) ? value : []) : value;
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1.5 pt-1">
      {(f.options || []).map((o) => (
        <label key={o} className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
          <input type={multi ? "checkbox" : "radio"} name={f.id} className="h-4 w-4"
            checked={multi ? vals.includes(o) : vals === o}
            onChange={() => onChange(multi ? (vals.includes(o) ? vals.filter((x) => x !== o) : [...vals, o]) : o)} />
          {o}
        </label>
      ))}
    </div>
  );
}

function FieldInput({ f, value, onChange, files }) {
  const set = (v) => onChange(f.id, v);
  switch (f.type) {
    case "file":
      return files?.client
        ? <FileUploader client={files.client} uploadPath={files.uploadPath} files={Array.isArray(value) ? value : []} onChange={set} testid={`file-field-${f.id}`} />
        : <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-500">Parents attach PDF or photos here (max 5 files, 10 MB each)</div>;
    case "paragraph":
      return <textarea rows={3} value={value || ""} onChange={(e) => set(e.target.value)} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-primary" />;
    case "checkbox":
      return <label className="inline-flex items-center gap-2 text-sm text-slate-700 h-11 cursor-pointer"><input type="checkbox" checked={!!value} onChange={(e) => set(e.target.checked)} className="h-5 w-5" /> Yes</label>;
    case "select":
      return (
        <select value={value || ""} onChange={(e) => set(e.target.value)} className={inputCls}>
          <option value="">Choose…</option>
          {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    case "radio":
      return <Choice f={f} value={value} onChange={set} />;
    case "multiselect":
      return <Choice f={f} value={value} onChange={set} multi />;
    case "signature":
      return <input value={value || ""} onChange={(e) => set(e.target.value)} placeholder="Type name or initials" className={`${inputCls} italic font-serif text-lg border-dashed`} />;
    case "initials":
      return (
        <div className="flex items-center gap-2">
          <input value={value || ""} onChange={(e) => set(e.target.value.replace(/[^a-z]/gi, "").toUpperCase().slice(0, 4))} placeholder="AB" maxLength={4}
            data-testid={`initials-input-${f.id}`} className="h-12 w-24 rounded-lg border-2 border-dashed border-indigo-300 bg-indigo-50/40 text-center font-serif italic text-xl font-bold tracking-widest uppercase outline-none focus:border-indigo-500" />
          <span className="text-[11px] text-slate-500">Initial here</span>
        </div>
      );
    case "number":
      return <input type="number" value={value ?? ""} onChange={(e) => set(e.target.value)} className={inputCls} />;
    case "phone":
      return <PhoneInput value={value || ""} onChange={set} placeholder="(555) 555-5555" className={inputCls} />;
    case "email":
      return <input type="email" value={value || ""} onChange={(e) => set(e.target.value)} placeholder="name@example.com" className={inputCls} />;
    case "date":
      return <input type="date" value={value || ""} onChange={(e) => set(e.target.value)} className={inputCls} />;
    default:
      return <input type="text" value={value || ""} onChange={(e) => set(e.target.value)} className={inputCls} />;
  }
}

export function FormRenderer({ fields = [], answers = {}, onChange = () => {}, testPrefix = "parent-field", files }) {
  return (
    <div className="grid grid-cols-12 gap-x-3 gap-y-4" data-testid="form-renderer">
      {fields.map((f) => {
        const span = SPAN_CLS[f.width] || SPAN_CLS[12];
        if (f.type === "heading") {
          return (
            <div key={f.id} className="col-span-12 pt-2 border-b border-slate-200 pb-1" data-testid={`${testPrefix}-${f.id}`}>
              <div className="font-display text-base font-bold text-slate-900">{f.label}</div>
              {f.help && <div className="text-xs text-slate-500">{f.help}</div>}
            </div>
          );
        }
        if (f.type === "statictext") {
          return <p key={f.id} className={`${span} text-sm text-slate-600 whitespace-pre-line`} data-testid={`${testPrefix}-${f.id}`}>{f.label}</p>;
        }
        if (f.type === "bullets" || f.type === "numbered") {
          const items = (f.label || "").split("\n").map((x) => x.trim()).filter(Boolean);
          const Tag = f.type === "bullets" ? "ul" : "ol";
          return (
            <Tag key={f.id} className={`${span} text-sm text-slate-700 pl-5 space-y-1 ${f.type === "bullets" ? "list-disc" : "list-decimal"}`} data-testid={`${testPrefix}-${f.id}`}>
              {items.map((it, i) => <li key={i}>{it}</li>)}
            </Tag>
          );
        }
        return (
          <div key={f.id} className={`${span} min-w-0`} data-testid={`${testPrefix}-${f.id}`}>
            <span className="block mb-1 text-xs font-semibold text-slate-700">
              {f.label || <span className="text-slate-300">Untitled</span>}{f.required && <span className="text-red-500"> *</span>}
            </span>
            <FieldInput f={f} value={answers[f.id]} onChange={onChange} files={files} />
            {f.help && <div className="mt-1 text-[11px] text-slate-500">{f.help}</div>}
          </div>
        );
      })}
    </div>
  );
}
